// src/hooks/useReferrer.js - SIMPLIFIED VERSION
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import DataBuyerService from '../lib/DataBuyerService';
import ReferrerManager from '../utils/ReferrerManager';

/**
 * Simplified hook for managing referrer and databuyer information
 * Just handles fetching and caching - no complex change detection
 */
export const useReferrer = () => {
  const [dataBuyer, setDataBuyer] = useState(null);
  const [referrer, setReferrer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const location = useLocation();

  /**
   * Fetches databuyer information for a referrer
   */
  const fetchDataBuyer = useCallback(async (referrerCode) => {
    if (!referrerCode) {
      return null;
    }

    try {
      // Check cache first
      const cached = ReferrerManager.getCachedDataBuyer(referrerCode);
      if (cached) {
        //console.log(`Using cached databuyer for referrer: ${referrerCode}`);
        return cached;
      }

      // Fetch from API
      console.log(`Fetching databuyer for referrer: ${referrerCode}`);
      const fetchedDataBuyer = await DataBuyerService.getDataBuyerByReferrer(referrerCode);
      
      if (fetchedDataBuyer && DataBuyerService.isValidDataBuyer(fetchedDataBuyer)) {
        // Cache the result
        ReferrerManager.setCachedDataBuyer(referrerCode, fetchedDataBuyer);
        return fetchedDataBuyer;
      }

      return null;

    } catch (error) {
      console.error('Error fetching databuyer:', error);
      throw error;
    }
  }, []);

  /**
   * Initializes referrer state
   */
  const initializeReferrer = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Get current referrer from URL
      const currentReferrer = ReferrerManager.getReferrerWithSubdomainFallback();
      setReferrer(currentReferrer);
      
      // Fetch databuyer if we have a referrer
      if (currentReferrer) {
        const fetchedDataBuyer = await fetchDataBuyer(currentReferrer);
        setDataBuyer(fetchedDataBuyer);
      } else {
        setDataBuyer(null);
      }

    } catch (error) {
      console.error('Error initializing referrer:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [fetchDataBuyer]);

  // Initialize on mount and when URL changes
  useEffect(() => {
    initializeReferrer();
  }, [location.search, initializeReferrer]);

  /**
   * Gets display configuration based on current databuyer and referrer
   */
  const displayConfig = useMemo(() => {
    return DataBuyerService.getDisplayConfig(dataBuyer, referrer);
  }, [dataBuyer, referrer]);

  /**
   * Manually refresh databuyer information (removes cache and refetches)
   */
  const refreshDataBuyer = useCallback(async () => {
    if (!referrer) return;
    
    try {
      setIsLoading(true);
      // Remove from cache and refetch
      ReferrerManager.removeCachedDataBuyer(referrer);
      const freshDataBuyer = await fetchDataBuyer(referrer);
      setDataBuyer(freshDataBuyer);
    } catch (error) {
      console.error('Error refreshing databuyer:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [referrer, fetchDataBuyer]);

  return {
    // State
    dataBuyer,
    referrer,
    isLoading,
    error,
    
    // Actions
    refreshDataBuyer,
    
    // Computed values
    displayConfig
  };
};

export default useReferrer;
